import { useState, useEffect, useMemo } from 'react';
import { X, FileText, CheckCircle, BookOpen, ExternalLink } from 'lucide-react';
import { createTicket } from '../../api/tickets';
import { getArticles } from '../../api/knowledge';
import Input from '../ui/input';
import Button from '../ui/button';
import { useAuth } from '../../context/AuthContext';
import { toast } from 'sonner';

const CreateTicketModal = ({
  isOpen,
  onClose,
  initialSubject = '',
  initialDescription = '',
  knowledgeArticleId = null,
  source = 'knowledge_base',
}) => {
  const { user } = useAuth();
  const [subject, setSubject] = useState(initialSubject);
  const [description, setDescription] = useState(initialDescription);
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);
  const [allArticles, setAllArticles] = useState([]);
  const [, setLoadingArticles] = useState(false);

  useEffect(() => {
    if (isOpen) {
      setSubject(initialSubject || '');
      setDescription(initialDescription || '');
      setSuccess(false);
      fetchArticles();
    }
  }, [isOpen, initialSubject, initialDescription]);

  const fetchArticles = async () => {
    try {
      setLoadingArticles(true);
      const response = await getArticles({ limit: 100 });
      if (response.success) {
        setAllArticles(response.data?.articles || []);
      }
    } catch (error) {
      console.error('Failed to load articles for suggestions:', error);
    } finally {
      setLoadingArticles(false);
    }
  };

  // Find related articles based on subject
  const suggestedArticles = useMemo(() => {
    if (!subject || subject.length < 3) return [];
    const subjectLower = subject.toLowerCase();
    return allArticles
      .filter(article => 
        article.title.toLowerCase().includes(subjectLower) ||
        (article.short_description && article.short_description.toLowerCase().includes(subjectLower)) ||
        (article.content && article.content.toLowerCase().includes(subjectLower))
      )
      .slice(0, 3);
  }, [subject, allArticles]);

  if (!isOpen) return null;

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!subject || !description) {
      toast.error('Subject and description are required');
      return;
    }

    try {
      setSubmitting(true);
      const payload = {
        subject,
        description,
        priority: 'medium',
        knowledge_article_id: knowledgeArticleId,
        source,
      };

      const response = await createTicket(payload);
      if (response.success) {
        setSuccess(true);
        toast.success('Ticket created successfully');
        setTimeout(() => {
          onClose();
          setSuccess(false);
        }, 2000);
      } else {
        toast.error(response.message || 'Failed to create ticket');
      }
    } catch (error) {
      console.error('Create ticket from knowledge base failed:', error);
      toast.error('Failed to create ticket');
    } finally {
      setSubmitting(false);
    }
  };

  if (success) {
    return (
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 dark:bg-black/80 backdrop-blur-sm p-4">
        <div className="bg-white dark:bg-slate-900 rounded-3xl shadow-2xl shadow-indigo-500/20 dark:shadow-black/40 w-full max-w-md p-8 text-center border border-gray-200/60 dark:border-slate-700/60">
          <div className="w-16 h-16 bg-gradient-to-br from-green-400 to-emerald-600 dark:from-green-500 dark:to-emerald-700 rounded-full flex items-center justify-center mx-auto mb-4 shadow-lg shadow-green-500/30">
            <CheckCircle className="w-8 h-8 text-white" />
          </div>
          <h2 className="text-2xl font-bold text-gray-900 dark:text-gray-100 mb-2">Ticket Created!</h2>
          <p className="text-gray-600 dark:text-gray-400 mb-6">
            Your support ticket has been created successfully. Our team will get back to you soon.
          </p>
          <Button onClick={onClose} className="w-full bg-gradient-to-r from-indigo-600 to-purple-600 hover:from-indigo-700 hover:to-purple-700 text-white shadow-lg shadow-indigo-500/20">
            Close
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 dark:bg-black/80 backdrop-blur-sm p-4">
      <div className="bg-white/95 dark:bg-slate-900/95 backdrop-blur-xl rounded-3xl shadow-2xl shadow-indigo-500/20 dark:shadow-black/40 w-full max-w-2xl max-h-[90vh] overflow-y-auto border border-gray-200/60 dark:border-slate-700/60">
        {/* Header */}
        <div className="sticky top-0 bg-gradient-to-r from-indigo-50 via-purple-50 to-pink-50 dark:from-slate-900 dark:via-indigo-950 dark:to-purple-950 border-b border-gray-200/60 dark:border-slate-700/60 px-6 py-5 flex items-center justify-between backdrop-blur-xl">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 bg-gradient-to-br from-indigo-500 to-purple-600 dark:from-indigo-600 dark:to-purple-700 rounded-xl flex items-center justify-center shadow-md shadow-indigo-500/20">
              <FileText className="w-6 h-6 text-white" />
            </div>
            <div>
              <h2 className="text-xl font-bold text-gray-900 dark:text-gray-100">Create Support Ticket</h2>
              <p className="text-sm text-gray-600 dark:text-gray-400">Get help from our support team</p>
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="p-2 rounded-xl hover:bg-gray-100 dark:hover:bg-slate-800 transition-colors text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200"
            aria-label="Close"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="p-6 md:p-8 space-y-6">
          {knowledgeArticleId && (
            <div className="bg-indigo-50/80 dark:bg-indigo-900/20 border border-indigo-200 dark:border-indigo-800 rounded-xl p-4">
              <p className="text-sm text-indigo-800 dark:text-indigo-300">
                <strong>Reference:</strong> This ticket is linked to Knowledge Base article #{knowledgeArticleId}
              </p>
            </div>
          )}

          <div>
            <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">
              Subject <span className="text-red-500">*</span>
            </label>
            <Input
              value={subject}
              onChange={(e) => setSubject(e.target.value)}
              placeholder="Brief summary of your issue"
              required
              className="w-full rounded-xl border-gray-200 dark:border-slate-700 bg-white dark:bg-slate-800 text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-500 dark:focus:border-indigo-500"
            />
            
            {/* Related Articles Suggestion */}
            {suggestedArticles.length > 0 && (
              <div className="mt-4 bg-blue-50/80 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-xl p-4">
                <div className="flex items-center gap-2 mb-3">
                  <BookOpen className="w-4 h-4 text-blue-600 dark:text-blue-400" />
                  <p className="text-sm font-semibold text-blue-800 dark:text-blue-300">
                    Did you mean one of these?
                  </p>
                </div>
                <div className="space-y-2">
                  {suggestedArticles.map((article) => (
                    <button
                      key={article.id}
                      type="button"
                      onClick={() => {
                        setSubject(article.title);
                        setDescription(
                          `Ticket created from article: ${article.title}\n\n${article.content || article.short_description || 'N/A'}`
                        );
                        toast.success('Article content loaded!');
                      }}
                      className="w-full text-left flex items-start gap-3 p-3 bg-white dark:bg-slate-800 rounded-lg hover:bg-blue-100 dark:hover:bg-blue-900/30 transition-colors border border-blue-200 dark:border-blue-700 group"
                    >
                      <FileText className="w-4 h-4 text-blue-600 dark:text-blue-400 mt-0.5 flex-shrink-0" />
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-blue-900 dark:text-blue-200 group-hover:text-blue-700 dark:group-hover:text-blue-100 line-clamp-2">
                          {article.title}
                        </p>
                        {article.short_description && (
                          <p className="text-xs text-blue-600 dark:text-blue-400 mt-1 line-clamp-1">
                            {article.short_description}
                          </p>
                        )}
                      </div>
                      <ExternalLink className="w-4 h-4 text-blue-500 opacity-0 group-hover:opacity-100 transition-opacity flex-shrink-0" />
                    </button>
                  ))}
                </div>
              </div>
            )}
          </div>

          <div>
            <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">
              Description <span className="text-red-500">*</span>
            </label>
            <textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              className="w-full min-h-[150px] border border-gray-200 dark:border-slate-700 rounded-xl px-4 py-3 text-gray-900 dark:text-gray-100 placeholder-gray-400 dark:placeholder-gray-500 bg-white dark:bg-slate-800 focus:outline-none focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-500 dark:focus:border-indigo-500 resize-y transition-all"
              placeholder="Describe your issue in detail..."
              required
            />
          </div>

          {user && (
            <div className="bg-gray-50/80 dark:bg-slate-800/60 rounded-xl p-4 border border-gray-200 dark:border-slate-700">
              <p className="text-sm text-gray-600 dark:text-gray-400">
                <strong className="text-gray-900 dark:text-gray-200">Requested by:</strong> {user.name} ({user.email})
              </p>
            </div>
          )}

          <div className="flex justify-end gap-3 pt-4 border-t border-gray-200 dark:border-slate-700">
            <Button
              type="button"
              variant="outline"
              onClick={onClose}
              disabled={submitting}
              className="border-gray-300 dark:border-slate-600 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-slate-800"
            >
              Cancel
            </Button>
            <Button 
              type="submit" 
              disabled={submitting} 
              className="bg-gradient-to-r from-indigo-600 to-purple-600 hover:from-indigo-700 hover:to-purple-700 text-white shadow-lg shadow-indigo-500/20"
            >
              {submitting ? 'Creating...' : 'Create Ticket'}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default CreateTicketModal;
